import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { verifyMessage } from 'ethers'
import { useAccount, useConnect, useDisconnect, useNetwork, useSignMessage } from 'wagmi'
import { useWeb3Modal } from '@web3modal/react'

import { Button } from 'primsell-shared-layer/components'
import Web3Service from 'primsell-shared-layer/core/services/web3'
import { useMobileScreen } from 'primsell-shared-layer/hooks'

import { BuyerActions, useModal, walletAddressSelector } from 'primsell-shared-layer/features'

import { fetchNonceSaga, validateSignature } from './lib'

import { SubSignMobileDialog } from './sub-sign-mobile-dialog'

import APP_CONFIG from 'primsell-shared-layer/core/config'

import { ModalVariantCommonE } from 'primsell-shared-layer/types'

import { CloseIcon, CoinbaseLogo2Icon, MetaMaskLogoIcon, WalletConnect2Icon } from 'primsell-shared-layer/assets/icons'
import * as S from './connect-wallet-dialog.styles'

export interface IConnectWalletDialogProps {
  open: boolean
  onSuccessConnect?: (wallet: string) => void
  handleClose: () => void
}

const ConnectWalletDialog = ({ open, handleClose, onSuccessConnect }: IConnectWalletDialogProps) => {
  const [date, setDate] = useState<string>('')

  const dispatch = useDispatch()
  const { openModal, closeModal, activeModal } = useModal()

  const { connect } = useConnect()
  const { isOpen, open: web3ModalOpen } = useWeb3Modal()
  const { isConnected, address } = useAccount()
  const { chain } = useNetwork()

  const isMobile = useMobileScreen()

  const recoveredAddress = useRef('')
  const isSignMessage = useRef(false)

  const { disconnect } = useDisconnect({
    onSuccess() {
      dispatch(BuyerActions.clearBuyerData())
      recoveredAddress.current = ''
    },
  })

  const [isSigning, setIsSigning] = useState(false)
  const walletAddress = useSelector(walletAddressSelector)

  const { isLoading, signMessage } = useSignMessage({
    onSuccess(signature: string, variables: { message: string | Uint8Array }) {
      isSignMessage.current = true
      const verifyAddress = verifyMessage(variables.message, signature)
      validateSignature({
        wallet: verifyAddress,
        signature,
        date: date,
        cb: ({ wallet }) => {
          recoveredAddress.current = wallet
          address && dispatch(BuyerActions.getBuyerDataRequest({ address }))
          onSuccessConnect && onSuccessConnect(wallet)
        },
      })
      setIsSigning(false)
    },
    onError() {
      disconnect()
    },
  })

  useEffect(() => {
    disconnect()
    isSignMessage.current = false
  }, [disconnect])

  useEffect(() => {
    if (!address) {
      recoveredAddress.current = ''
      setIsSigning(false)
    }
  }, [address])

  useEffect(() => {
    if (isConnected && !isMobile && !recoveredAddress.current) {
      setIsSigning(true)
    }
  }, [isConnected, isMobile])

  useEffect(() => {
    if (isConnected && isSigning && activeModal !== ModalVariantCommonE.CHANGE_NETWORK_MODAL) {
      fetchNonceSaga(address as string, ({ message, dateFetched }) => {
        setDate(dateFetched)
        signMessage({ message: message as string })

        const timeout = 30000 // 30sec
        setTimeout(() => {
          if (isSignMessage.current) return
          // Dou to the issue with signMessage event at useSignMessage hook, the pulling event was skiped and we could't finilize connect.
          // Workaround resolution that we can do is check flow and in case we stopped at signMessage then do disconnect the wallet and ask for next connection
          disconnect()
          isSignMessage.current = true
          // TODO: modify alert as common error messages handler
          alert('Warning: Wallet session collisions, please retry.')
        }, timeout)
      })
    }
  }, [isConnected, isSigning, address, activeModal, signMessage, disconnect])

  useEffect(() => {
    if (chain?.id && APP_CONFIG.chains.some(({ chainId }: any) => chain?.id !== chainId) && walletAddress) {
      openModal({ variant: ModalVariantCommonE.CHANGE_NETWORK_MODAL })
    } else {
      if (activeModal === ModalVariantCommonE.CHANGE_NETWORK_MODAL) {
        closeModal()
      }
    }
  }, [activeModal, chain, closeModal, openModal, walletAddress])

  if (!Web3Service.metamaskService.connector || !Web3Service.coinbaseService.connector) {
    return null
  }

  return (
    <>
      <S.Dialog open={open} onClose={handleClose}>
        <S.CloseButton onClick={handleClose}>
          <CloseIcon />
        </S.CloseButton>
        <S.DialogHeader>
          <S.DialogTitle>Connect wallet</S.DialogTitle>
        </S.DialogHeader>
        <S.DialogContent>
          {!isMobile ? (
            <Button
              color="grey"
              variant="filled"
              fullWidth
              icon={<MetaMaskLogoIcon />}
              disabled={!Web3Service.metamaskService.connector.ready}
              key={Web3Service.metamaskService.connector.id}
              onClick={() => {
                handleClose()
                connect({ connector: Web3Service.metamaskService.connector })
              }}
            >
              MetaMask
            </Button>
          ) : null}

          <Button
            color="grey"
            variant="filled"
            fullWidth
            icon={<WalletConnect2Icon />}
            disabled={isOpen}
            key={'__walletconnect'}
            onClick={() => {
              handleClose()
              web3ModalOpen()
            }}
          >
            WalletConnect
          </Button>
          <Button
            color="grey"
            variant="filled"
            fullWidth
            icon={<CoinbaseLogo2Icon />}
            disabled={!Web3Service.coinbaseService.connector.ready}
            key={Web3Service.coinbaseService.connector.id}
            onClick={() => {
              handleClose()
              connect({ connector: Web3Service.coinbaseService.connector })
            }}
          >
            Coinbase Wallet
          </Button>
        </S.DialogContent>
      </S.Dialog>

      {isMobile && (
        <SubSignMobileDialog
          isSigning={isSigning}
          handleSign={() => setIsSigning(true)}
          data={recoveredAddress.current}
          isLoading={isLoading}
        />
      )}
    </>
  )
}

export { ConnectWalletDialog }
